/**
 * Phase 4.0 / 4.1 — KRO Phone System v2: WebRTC Outbound Proof (dev page).
 *
 * Hidden internal-only dev page at /dev/webrtc.
 *
 * Refactored in 4.1 to use the shared `getWebRTC()` singleton from
 * `lib/webrtcClient.js` so calls placed here also drive the global
 * Active Call Bar — and the Inbox split-button shares the same SDK
 * instance. Eliminates the chance of two independent SIP registrations
 * fighting for the same JWT.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Phone, PhoneOff, Plug, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getWebRTC } from '../../lib/webrtcClient';

const STATE_COLORS = {
  idle:       'bg-slate-200 text-slate-700',
  trying:     'bg-amber-100 text-amber-800',
  requesting: 'bg-amber-100 text-amber-800',
  ringing:    'bg-blue-100 text-blue-800',
  early:      'bg-blue-100 text-blue-800',
  active:     'bg-green-100 text-green-800',
  held:       'bg-amber-100 text-amber-800',
  hangup:     'bg-rose-100 text-rose-800',
  destroy:    'bg-rose-100 text-rose-800',
  purge:      'bg-rose-100 text-rose-800',
};

const isE164 = (s) => typeof s === 'string' && /^\+\d{8,15}$/.test(s.trim());

export default function WebRTCDev() {
  const [state, setState] = useState(getWebRTC().getState());
  const [phone, setPhone] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = getWebRTC().subscribe((s) => {
      setState(s);
      if (s.error && s.error !== errorMsg) setErrorMsg(s.error);
      if (s.activeCall) {
        const ts = new Date().toLocaleTimeString();
        setLogs((prev) => {
          const line = `[${ts}] state=${s.activeCall.state} dur=${s.activeCall.durationSecs}s callId=${s.activeCall.callId}`;
          if (prev[prev.length - 1] === line) return prev;
          return [...prev.slice(-49), line];
        });
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushLog = (line) => {
    const ts = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev.slice(-49), `[${ts}] ${line}`]);
  };

  const onRegister = async () => {
    setErrorMsg('');
    setBusy(true);
    try {
      pushLog('→ register()');
      await getWebRTC().register();
      pushLog('✓ registered');
    } catch (err) {
      setErrorMsg(err.message || String(err));
      pushLog(`✗ register failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const onDial = async () => {
    setErrorMsg('');
    if (!isE164(phone)) {
      setErrorMsg('Enter a destination in E.164 (e.g. +14155551234).');
      return;
    }
    try {
      pushLog(`→ placeCall(${phone.trim()})`);
      await getWebRTC().placeCall({
        destination: phone.trim(),
        conversationId: conversationId.trim() || null,
        surface: 'dev-webrtc-page',
      });
    } catch (err) {
      const t = err?.telnyx;
      if (t) {
        const blob = [
          `Telnyx rejected the call (HTTP ${t.status || '?'})`,
          `detail: ${t.detail || '(none)'}`,
          `request: ${JSON.stringify(t.request || {}, null, 2)}`,
          `response body: ${t.body || '(empty)'}`,
        ].join('\n');
        setErrorMsg(blob);
        pushLog(`✗ telnyx ${t.status}: ${t.detail || '(no detail)'}`);
      } else {
        setErrorMsg(err?.message || String(err));
        pushLog(`✗ placeCall failed: ${err?.message || err}`);
      }
    }
  };

  const onHangup = () => {
    pushLog('· hangup() requested');
    getWebRTC().hangup();
  };

  const onManualAnswer = () => {
    pushLog('· manual answer() requested');
    getWebRTC().answer();
  };

  const callState = state.activeCall?.state || 'idle';
  const inCall = state.activeCall && !['hangup', 'destroy', 'purge'].includes(callState);
  const registered = state.status === 'registered';

  return (
    <div className="container mx-auto max-w-4xl py-8 px-4 space-y-6" data-testid="webrtc-dev-page">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
            INTERNAL · DEV PROOF
          </Badge>
          <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-700">
            Phase 4.0 / 4.1 · WebRTC Outbound
          </Badge>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">KRO Phone v2 — Browser Softphone Proof</h1>
        <p className="text-sm text-slate-600 max-w-2xl">
          Hidden test surface. Mints an ephemeral Telnyx JWT for your account, registers
          this browser as a WebRTC endpoint, and places one outbound call. Outbound
          caller-ID is forced to your company's KRO number. Calls placed here also
          drive the global Active Call Bar at the bottom-right.
        </p>
      </header>

      {errorMsg ? (
        <div
          className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex gap-2 items-start"
          data-testid="webrtc-error"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed m-0">{errorMsg}</pre>
        </div>
      ) : null}

      {/* Step 1 — Register */}
      <Card data-testid="webrtc-step-register">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-4 w-4" /> 1. Register WebRTC client
          </CardTitle>
          <CardDescription>
            Fetches a fresh Telnyx JWT from <code className="text-xs">/api/webrtc/token</code> and connects.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={onRegister}
              disabled={busy || registered}
              data-testid="webrtc-register-btn"
            >
              {registered ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Registered</> : 'Register'}
            </Button>
            {state.displayCallerId ? (
              <Badge variant="secondary" data-testid="webrtc-caller-id">
                Caller-ID: <span className="ml-1 font-mono">{state.displayCallerId}</span>
              </Badge>
            ) : null}
            <Badge
              className={STATE_COLORS[callState] || STATE_COLORS.idle}
              data-testid="webrtc-call-state"
            >
              {registered ? `state: ${callState}` : `status: ${state.status}`}
            </Badge>
          </div>
          {registered && !state.displayCallerId ? (
            <p className="text-xs text-amber-700">
              ⚠ No KRO channel number found for this company. Outbound dialing will be
              blocked until a channel is configured in Settings → Communication.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Step 2 — Dial */}
      <Card data-testid="webrtc-step-dial">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-4 w-4" /> 2. Place outbound call
          </CardTitle>
          <CardDescription>
            Type any E.164 destination. The customer will see the KRO caller-ID above.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dest">Destination (E.164)</Label>
              <Input
                id="dest"
                placeholder="+14155551234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={inCall}
                data-testid="webrtc-destination-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conv">Conversation ID (optional)</Label>
              <Input
                id="conv"
                placeholder="leave blank to skip Inbox bubble"
                value={conversationId}
                onChange={(e) => setConversationId(e.target.value)}
                disabled={inCall}
                data-testid="webrtc-conversation-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={onDial}
              disabled={!registered || inCall || !isE164(phone)}
              data-testid="webrtc-dial-btn"
            >
              <Phone className="h-4 w-4 mr-1.5" /> Dial
            </Button>
            <Button
              variant="destructive"
              onClick={onHangup}
              disabled={!inCall}
              data-testid="webrtc-hangup-btn"
            >
              <PhoneOff className="h-4 w-4 mr-1.5" /> Hang Up
            </Button>
            <Button
              variant="secondary"
              onClick={onManualAnswer}
              disabled={!inCall || callState === 'active'}
              data-testid="webrtc-manual-answer-btn"
              title="Force-answer the inbound WebRTC leg (diagnostic)"
            >
              Force Answer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 — Trace */}
      <Card data-testid="webrtc-step-trace">
        <CardHeader>
          <CardTitle>3. Trace</CardTitle>
          <CardDescription>
            Live SDK + REST event log. Useful for diagnosing connect / dial failures.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="rounded-md bg-slate-950 text-slate-100 font-mono text-xs p-3 max-h-72 overflow-y-auto"
            data-testid="webrtc-log"
          >
            {logs.length === 0 ? (
              <div className="text-slate-500">No events yet.</div>
            ) : (
              logs.map((l, i) => <div key={i}>{l}</div>)
            )}
          </div>
        </CardContent>
      </Card>

      <Separator />
      <p className="text-xs text-slate-500">
        Production default remains the native <code>tel:</code> dialer.
        This page is gated by <code>REACT_APP_WEBRTC_DEV=true</code> in the frontend env.
      </p>
    </div>
  );
}
